import { useMediaQuery, useTheme } from '@mui/material';

import {
	BannerAlertMessagesCollection,
	BannerMobileAlertMessagesCollection,
	ContentTypeRichText,
} from '@/graphql/__generated/sdk';
import { useFacilityContext } from '@/context';

import AlertBanner from './AlertBanner/AlertBanner';

/**
 * Checks if a facility code should match a comma-separated list.
 *
 * @param list - A comma-separated list of facility codes (e.g., "1234,4567,8971")
 * @param code - The facility code to check (e.g., "1234")
 * @returns true if the list is empty OR the code is found in the list; otherwise false.
 */
const matchesFacilityCode = (list: string, code: string | undefined): boolean => {
	// Trim and check for empty/null list → treat as "show on all"
	if (!code || !list || list.trim().length === 0) {
		return true;
	}

	// Split by comma, trim spaces, and compare
	const codes = list
		.split(',')
		.map((c) => c.trim())
		.filter(Boolean);

	// If list is still empty after trimming, treat as show all
	if (codes.length === 0) {
		return true;
	}

	return codes.includes(code);
};

export interface IBanner {
	variant: 'Alert Banner' | null; // Type of the banner;
	alertMessagesCollection?: BannerAlertMessagesCollection;
	mobileAlertMessagesCollection?: BannerMobileAlertMessagesCollection;
	dataTestId?: string;
}

export default function Banner({
	variant = 'Alert Banner',
	alertMessagesCollection,
	mobileAlertMessagesCollection,
	dataTestId,
}: Readonly<IBanner>) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const { facilityCode } = useFacilityContext();

	const alertMessages = isSmallScreen ? mobileAlertMessagesCollection?.items : alertMessagesCollection?.items;
	const filteredAlertMessages = alertMessages?.filter((message) => {
		return matchesFacilityCode(message?.facilitySegmentCollection || '', facilityCode);
	});

	return variant === 'Alert Banner' && alertMessages ? (
		<AlertBanner dataTestId={dataTestId} alertMessages={filteredAlertMessages as ContentTypeRichText[]} />
	) : null;
}
